import {
  Alert,
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  Card,
  Form,
  newToast,
  PageRoute,
  pn,
  Stack,
  Typography,
  useParams,
  useTheme,
  useValidationErrors,
} from "@compass/core";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { SubmitButton } from "components";
import { ForgotCredential, ForgotCredentialType, ForgotCredentialViewResource } from "generated/openApi";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { loginPageRoute } from "../LoginPage/LoginPage";
import { AuthField } from "../components/AuthField";
import { AuthPageWrapper } from "../components/AuthPageWrapper";
import { pillButtonStyles, authColors, pillToggleGroupStyles } from "../styles/authStyles";

const routeName = "forgotCredential";

class OptionalParams {
  tenantName?: string = "";
}

export const forgotCredentialPageRoute: PageRoute<undefined, OptionalParams> = {
  handle: "Forgot Credentials",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export const ForgotCredentialPage = () => {
  const { optionalParams } = useParams(routeName, undefined, OptionalParams);
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [forgotCredential, setForgotCredential] = useState<ForgotCredential>({
    tenantName: optionalParams.tenantName,
    forgotCredentialType: ForgotCredentialType.ForgotPassword,
  } as ForgotCredential);

  const [viewResource, setViewResource] = useState<ForgotCredentialViewResource>({});
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [isSuccess, setIsSuccess] = useState(false);

  const properties = viewResource.properties || [];

  const handleFieldChange = (name: string, value: string) => {
    setForgotCredential((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    ApiService.auth.getForgotCredentialViewResource().then(setViewResource);
  }, []);

  const post = () => {
    setValidationErrors({});

    ApiService.auth
      .postForgotCredential(forgotCredential)
      .then(() => {
        setIsSuccess(true);
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.data);
      });
  };

  const handleCredentialTypeChange = (_: React.MouseEvent, value: ForgotCredentialType | null) => {
    if (value !== null) {
      setForgotCredential((prev) => ({ ...prev, forgotCredentialType: value }));
    }
  };

  const handleReturnToLogin = () => {
    navigate({ pathname: `/${loginPageRoute.getPath()}` });
  };

  const generalErrors = validationErrors?.[pn<ForgotCredential>("generalValidationErrors")];

  return (
    <AuthPageWrapper>
      <Card sx={{ maxWidth: "550px", width: "100%" }}>
        <Stack px={3} py={7} spacing="lg">
          {isSuccess ? (
            <Stack spacing="lg" alignItems="center">
              <CheckCircleIcon 
                sx={{ 
                  fontSize: 64, 
                  color: authColors.primary.main 
                }} 
              />
              <Stack spacing="sm" alignItems="center">
                <Typography variant="h4" fontWeight={theme.typography.fontWeightMedium}>
                  Email Sent!
                </Typography>
                <Typography variant="subtitle1" textAlign="center">
                  Credential recovery instructions have been sent to the email address on file.
                </Typography>
              </Stack>
              <SubmitButton
                onClick={handleReturnToLogin}
                label="Return to Login"
                fullWidth
                sx={{ height: "50px", ...pillButtonStyles, mt: 2 }}
              />
            </Stack>
          ) : (
            <>
              <Stack spacing="sm" alignItems="center">
                <Typography variant="h4" fontWeight={theme.typography.fontWeightMedium}>
                  Forgot Username/Password
                </Typography>
                <Typography variant="subtitle2">
                  Forgot your username or password? Please enter your email. An email with instructions will be sent to you.
                </Typography>
              </Stack>

              <Form>
                <Stack spacing="md">
                  {generalErrors && <Alert severity="error">{generalErrors[0]}</Alert>}
                  <ToggleButtonGroup
                    value={forgotCredential.forgotCredentialType}
                    exclusive
                    onChange={handleCredentialTypeChange}
                    fullWidth
                    sx={pillToggleGroupStyles}
                  >
                    <ToggleButton value={ForgotCredentialType.ForgotPassword}>
                      Forgot My Password
                    </ToggleButton>
                    <ToggleButton value={ForgotCredentialType.ForgotUsername}>
                      Forgot My Username
                    </ToggleButton>
                  </ToggleButtonGroup>

                  <AuthField
                    name="tenantName"
                    value={forgotCredential.tenantName || ""}
                    onChange={handleFieldChange}
                    properties={properties}
                    validationErrors={validationErrors}
                  />
                  <AuthField
                    name="email"
                    value={forgotCredential.email || ""}
                    onChange={handleFieldChange}
                    properties={properties}
                    validationErrors={validationErrors}
                  />

                  <SubmitButton
                    onClick={post}
                    label="Send Me Email"
                    fullWidth
                    sx={{ height: "50px", ...pillButtonStyles }}
                  />
                </Stack>
              </Form>
            </>
          )}
        </Stack>
      </Card>
    </AuthPageWrapper>
  );
};